import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  Settings, Video, CheckCircle, XCircle, AlertCircle,
  Users, UserPlus, Upload, Trash2, KeyRound, FileSpreadsheet, FileDown, Pencil
} from "lucide-react";
import { API } from "@/App";

export function JoinRequestsCard({ classId, classData, headers, onUpdate }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ require_approval: false });
  const [meetLink, setMeetLink] = useState(classData?.meet_link || "");
  const [savingMeet, setSavingMeet] = useState(false);

  useEffect(() => {
    fetchRequests();
    fetchSettings();
    setMeetLink(classData?.meet_link || "");
  }, [classId, classData]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/classes/${classId}/join-requests`, { headers });
      setRequests(response.data);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/classes/${classId}/settings`, { headers });
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await axios.post(`${API}/join-requests/${requestId}/approve`, {}, { headers });
      toast.success("Student approved!");
      fetchRequests();
      onUpdate();
    } catch (error) {
      toast.error("Failed to approve request");
    }
  };

  const handleReject = async (requestId) => {
    try {
      await axios.post(`${API}/join-requests/${requestId}/reject`, { reason: "" }, { headers });
      toast.success("Request rejected");
      fetchRequests();
    } catch (error) {
      toast.error("Failed to reject request");
    }
  };

  const toggleApproval = async (checked) => {
    try {
      await axios.put(`${API}/classes/${classId}/settings`, { require_approval: checked }, { headers });
      setSettings({ ...settings, require_approval: checked });
      toast.success(checked ? "Approval required for new students" : "Students can join directly");
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const handleSaveMeetLink = async () => {
    setSavingMeet(true);
    try {
      await axios.put(`${API}/classes/${classId}/settings`, { meet_link: meetLink }, { headers });
      toast.success("Meeting link updated!");
      onUpdate();
    } catch (error) {
      toast.error("Failed to update meeting link");
    } finally {
      setSavingMeet(false);
    }
  };

  return (
    <Card className="border-border/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Class Settings
            </CardTitle>
            <CardDescription>Manage meeting link and join settings</CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <Label htmlFor="require-approval" className="text-sm">Require Approval</Label>
            <Switch
              id="require-approval"
              checked={settings.require_approval}
              onCheckedChange={toggleApproval}
              data-testid="require-approval-switch"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Video className="w-4 h-4" />
            Meeting Link
          </Label>
          <div className="flex gap-2">
            <Input
              data-testid="meet-link-input"
              placeholder="https://meet.google.com/... or https://zoom.us/..."
              value={meetLink}
              onChange={(e) => setMeetLink(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleSaveMeetLink} disabled={savingMeet} className="rounded-lg" data-testid="save-meet-btn">
              {savingMeet ? "Saving..." : "Save"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Students will see a "Join Meeting" button when a link is set</p>
        </div>

        <div className="border-t border-border/50 pt-6">
          <h4 className="font-medium text-foreground mb-4">Join Requests</h4>
          {!settings.require_approval ? (
            <div className="text-center py-6 text-muted-foreground">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
              <p>Students can join directly using the class code</p>
              <p className="text-sm">Enable "Require Approval" to review join requests first</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
              <p>No pending join requests</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => (
                <div
                  key={req.request_id}
                  className="flex items-center justify-between p-3 rounded-xl border border-border/50 bg-yellow-50/50"
                  data-testid={`join-request-${req.request_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                      <span className="text-yellow-700 font-medium">
                        {req.student_name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{req.student_name}</p>
                      <p className="text-sm text-muted-foreground">{req.student_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(req.request_id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`reject-${req.request_id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleApprove(req.request_id)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`approve-${req.request_id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function StudentManagementCard({ classId, classData, students, headers, onUpdate }) {
  const [createDialog, setCreateDialog] = useState(false);
  const [bulkImportDialog, setBulkImportDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [newStudent, setNewStudent] = useState({ name: "", email: "", password: "" });
  const [renameDialog, setRenameDialog] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null); // { user_id, name, email, original_name, display_name_override }
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const bulkFileRef = useRef(null);

  const openRenameDialog = (student) => {
    setRenameTarget(student);
    setRenameValue(student.name || "");
    setRenameDialog(true);
  };

  const handleRenameStudent = async () => {
    if (!renameTarget) return;
    const trimmed = renameValue.trim();
    if (!trimmed) { toast.error("Name cannot be empty"); return; }
    setRenaming(true);
    try {
      await axios.put(
        `${API}/classes/${classId}/students/${renameTarget.user_id}/name`,
        { name: trimmed },
        { headers }
      );
      const isRevert = renameTarget.original_name && trimmed === renameTarget.original_name;
      toast.success(isRevert ? "Reverted to original name" : "Student renamed for this class");
      setRenameDialog(false);
      setRenameTarget(null);
      setRenameValue("");
      onUpdate && onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rename");
    } finally { setRenaming(false); }
  };

  const handleCreateStudent = async () => {
    if (!newStudent.name || !newStudent.email || !newStudent.password) {
      toast.error("Please fill all fields");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/students/create`, {
        name: newStudent.name, email: newStudent.email, password: newStudent.password, class_id: classId
      }, { headers });
      toast.success("Student account created! A welcome email with login credentials has been sent.");
      setNewStudent({ name: "", email: "", password: "" });
      setCreateDialog(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create student");
    } finally {
      setCreating(false);
    }
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/classes/${classId}/students/bulk-import`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      setImportResult(response.data);
      toast.success(`Imported ${response.data.successful} students! Welcome emails sent.`);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import students");
    } finally {
      setImporting(false);
      if (bulkFileRef.current) bulkFileRef.current.value = "";
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/classes/${classId}/export/csv`, { headers, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${classData?.name || 'class'}_grades.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Grades exported to CSV!");
    } catch (error) {
      toast.error("Failed to export grades");
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info("Generating PDF report...");
      const response = await axios.get(`${API}/classes/${classId}/export/pdf`, { headers, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${classData?.name || 'class'}_grades_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF report downloaded!");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const downloadCSVTemplate = () => {
    const csvContent = "name,email,password\nJohn Doe,john@example.com,password123\nJane Smith,jane@example.com,password456";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'student_import_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleRemoveStudent = async (studentId, studentName) => {
    const deleteAccount = confirm(`Remove ${studentName} from this class?\n\nClick OK to just remove from class.\nThe student can still be added to other classes.`);
    if (deleteAccount === null) return;
    const shouldDeleteAccount = confirm(`Also DELETE the student account entirely?\n\nClick OK to delete the account (email can be reused).\nClick Cancel to keep the account.`);
    try {
      await axios.delete(`${API}/classes/${classId}/students/${studentId}?delete_account=${shouldDeleteAccount}`, { headers });
      toast.success(shouldDeleteAccount ? "Student removed and account deleted" : "Student removed from class");
      onUpdate();
    } catch (error) {
      toast.error("Failed to remove student");
    }
  };

  const handleResetPassword = async (studentId, studentName) => {
    const newPassword = prompt(`Enter new password for ${studentName}:`);
    if (!newPassword) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await axios.put(`${API}/students/${studentId}/reset-password`, { new_password: newPassword }, { headers });
      toast.success(`Password reset for ${studentName}. New password: ${newPassword}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let password = "";
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewStudent({ ...newStudent, password });
  };

  return (
    <Card className="border-border/50">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <CardTitle className="font-['Manrope']">Students</CardTitle>
            <CardDescription>{students.length} students enrolled · Join Code: <strong>{classData?.join_code}</strong></CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExportCSV} className="rounded-full" data-testid="export-csv-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF} className="rounded-full" data-testid="export-pdf-btn">
              <FileDown className="w-4 h-4 mr-2" />
              Export PDF
            </Button>

            <Dialog open={bulkImportDialog} onOpenChange={setBulkImportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full" data-testid="bulk-import-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Bulk Import
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Manrope']">Bulk Import Students</DialogTitle>
                  <DialogDescription>Upload a CSV file with student details</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm font-medium mb-2">CSV Format Required:</p>
                    <code className="text-xs bg-background p-2 rounded block">name,email,password</code>
                    <Button variant="link" size="sm" onClick={downloadCSVTemplate} className="mt-2 p-0 h-auto">
                      Download template
                    </Button>
                  </div>
                  <div className="border-2 border-dashed border-border rounded-xl p-6 text-center">
                    <input type="file" ref={bulkFileRef} accept=".csv" onChange={handleBulkImport} className="hidden" />
                    <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-3">Drop your CSV file here or click to browse</p>
                    <Button onClick={() => bulkFileRef.current?.click()} disabled={importing} className="rounded-full">
                      {importing ? "Importing..." : "Select CSV File"}
                    </Button>
                  </div>
                  {importResult && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>{importResult.successful} students imported successfully</span>
                      </div>
                      {importResult.failed > 0 && (
                        <div className="text-sm text-destructive">
                          <p className="font-medium">{importResult.failed} failed:</p>
                          <ul className="list-disc list-inside text-xs mt-1">
                            {importResult.error_details?.slice(0, 5).map((err, i) => (
                              <li key={i}>{err.email || err.name}: {err.error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {importResult.success_details?.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-2">
                          <p className="font-medium">Created accounts:</p>
                          <div className="max-h-32 overflow-y-auto mt-1 space-y-1">
                            {importResult.success_details.map((s, i) => (
                              <div key={i} className="bg-muted p-1 rounded text-xs">
                                {s.email} - Password: <code>{s.password}</code>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={createDialog} onOpenChange={setCreateDialog}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-primary text-primary-foreground" data-testid="create-student-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Student
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Manrope']">Create Student Account</DialogTitle>
                  <DialogDescription>Create a new student and add them to this class</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Student Name *</Label>
                    <Input data-testid="student-name-input" placeholder="John Doe" value={newStudent.name} onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input data-testid="student-email-input" type="email" placeholder="student@example.com" value={newStudent.email} onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Password *</Label>
                      <Button type="button" variant="ghost" size="sm" onClick={generatePassword} className="text-xs">
                        Generate
                      </Button>
                    </div>
                    <Input data-testid="student-password-input" type="text" placeholder="Enter password" value={newStudent.password} onChange={(e) => setNewStudent({ ...newStudent, password: e.target.value })} />
                    {newStudent.password && (
                      <p className="text-xs text-muted-foreground">Share this password with the student</p>
                    )}
                  </div>
                  <Button onClick={handleCreateStudent} className="w-full rounded-full bg-primary text-primary-foreground" disabled={creating} data-testid="submit-create-student">
                    {creating ? "Creating..." : "Create Student Account"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {students.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No students enrolled yet</p>
            <p className="text-sm text-muted-foreground mb-4">Create student accounts or share the join code</p>
          </div>
        ) : (
          <div className="space-y-3">
            {students.map((student) => (
              <div
                key={student.user_id}
                className="flex items-center justify-between p-3 rounded-xl border border-border/50 group"
                data-testid={`student-${student.user_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-primary font-medium">
                      {student.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-foreground truncate">{student.name}</p>
                      {student.display_name_override && (
                        <span
                          className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5"
                          title={`Original account name: ${student.original_name}`}
                        >
                          Renamed
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{student.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-primary" onClick={() => openRenameDialog(student)} data-testid={`rename-student-${student.user_id}`} title="Rename for this class">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-primary" onClick={() => handleResetPassword(student.user_id, student.name)} data-testid={`reset-password-${student.user_id}`} title="Reset Password">
                    <KeyRound className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => handleRemoveStudent(student.user_id, student.name)} data-testid={`remove-student-${student.user_id}`} title="Remove Student">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={renameDialog} onOpenChange={(open) => { setRenameDialog(open); if (!open) { setRenameTarget(null); setRenameValue(""); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Rename Student for This Class</DialogTitle>
            <DialogDescription>
              This changes the name shown for this student in <strong>this class only</strong>. Useful when siblings share an email but attend different classes. The student's account name and how other classes see them stay unchanged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-muted-foreground">Email</Label>
              <p className="text-sm font-mono">{renameTarget?.email}</p>
            </div>
            {renameTarget?.original_name && renameTarget?.display_name_override && (
              <div>
                <Label className="text-xs text-muted-foreground">Original account name</Label>
                <p className="text-sm">{renameTarget.original_name}</p>
              </div>
            )}
            <div>
              <Label htmlFor="rename-input">Display name in this class</Label>
              <Input
                id="rename-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                maxLength={80}
                onKeyDown={(e) => { if (e.key === "Enter") handleRenameStudent(); }}
                data-testid="rename-input"
                autoFocus
              />
              {renameTarget?.original_name && renameValue.trim() === renameTarget.original_name && (
                <p className="text-xs text-muted-foreground mt-1">
                  Saving will revert this student to their original account name in this class.
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setRenameDialog(false)} data-testid="cancel-rename-btn">Cancel</Button>
            <Button
              onClick={handleRenameStudent}
              disabled={renaming || !renameValue.trim()}
              className="bg-primary text-primary-foreground"
              data-testid="confirm-rename-btn"
            >
              {renaming ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
